import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { setModal, selectModal } from "../../../redux/modalSlice";
import { useForm, Controller } from 'react-hook-form'
import { useRequest } from '../../../hooks/UseRequest'
import { setNotification } from '../../../redux/notificationSlice'
import Loading from '../../../components/Loading'
import { selectDropdownOptions } from '../../../redux/dropdownSlice'
import DeferCountOverrideForm from './DeferCountOverrideForm'
import CreditForm from './CreditForm'
import DiscountForm from './DiscountForm';
import SettlementOfferForm from './SettlementOfferForm';
import LeadTermOverrideForm from './LeadTermOverrideForm';
import RefinanceForm from './RefinanceForm';
import BankAccountForm from "./BankAccountForm";
import { triggerRerender } from "../../../redux/rerenderSlice";

export default function ManagerApprovalModal() {
  const dispatch = useDispatch();
  const modal = useSelector(selectModal);
  const { setError, handleSubmit, control, formState: { errors }, reset } = useForm();
  const [saving, setSaving] = useState(false)
  const request = useRequest()
  const [responseData, setResponseData] = useState(null)
  const dropdownOptions = useSelector(selectDropdownOptions)
  const [managerApprovalType, setManagerApprovalType] = useState("")
  const [managerApprovalStatus, setManagerApprovalStatus] = useState("")
  const [approvalType, setApprovalType] = useState("")
  const [actionDisabled, setActionDisabled] = useState(false)
  // variable to check if the data is loaded
  const [isApprovalDataLoaded, setIsApprovalDataLoaded] = useState(false)

  useEffect(() => {
    return () => {
      setResponseData(null);
      setManagerApprovalType("");
      setIsApprovalDataLoaded(false)
    };
  }, []);

  useEffect(() => {
    if (modal.options?.recordId && modal.type === 'manager_approval_modal') {
      setResponseData(null);
      getRecord(modal.options?.recordId)
      setIsApprovalDataLoaded(false)
    }
  }, [modal.options?.recordId])

  const getRecord = async (recordId) => {
    request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manager_approval/${recordId}/detail`)
    try {
      const response = await request.get()
      if (response.status === 200 || response.status === 201) {
        setResponseData(response?.data?.data)
        setManagerApprovalType(dropdownOptions.find((x) => x?.id === response?.data?.data?.mgr_approval_type_dropdown_option_id))
        setManagerApprovalStatus(dropdownOptions.find((x) => x?.id === response?.data?.data?.mgr_approval_status_dropdown_option_id))
        setIsApprovalDataLoaded(true)
      } else {
        dispatch(setModal({ is_active: false }));
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      dispatch(setModal({ is_active: false }));
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  const handleCancel = () => {
    dispatch(setModal({ is_active: false }));
    reset();
    setResponseData(null);
    setManagerApprovalType("");
    setIsApprovalDataLoaded(false)
  };

  const onSubmit = async (formData) => {
    setSaving(true)
    const recordId = modal.options?.recordId
    try {
      let body = {}

      if (approvalType == "approved") {
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manager_approval/${recordId}/approve`)
        body = {
          mgr_approval_status_dropdown_option_id: dropdownOptions.find((x) => x?.code_key === 'mgr_approval_status_approved')?.id,
          description: formData.description,
          related_record_fields: {},
        }
        if (managerApprovalType?.code_key == 'mgr_approval_type_credit') {
          body.related_record_fields.principal_amount = formData.principal_amount
          body.related_record_fields.interest_amount = formData.interest_amount
          body.related_record_fields.late_fee = formData.late_fee
          body.related_record_fields.nsf_fee = formData.nsf_fee
          body.related_record_fields.credit_reimbursement_amount = formData.credit_reimbursement_amount
        } else if (managerApprovalType?.code_key == 'mgr_approval_type_discount') {
          // body.related_record_fields.principal_amount = formData.principal_amount
          // body.related_record_fields.interest_amount = formData.interest_amount
          body.related_record_fields.late_fee_discount = formData.late_fee_discount
          body.related_record_fields.nsf_fee_discount = formData.nsf_fee_discount
        } else if (managerApprovalType?.code_key == 'mgr_approval_type_settlement_offer') {
          body.related_record_fields.discount_percent = formData.discount_percent
        } else if (managerApprovalType?.code_key == 'mgr_approval_type_max_principal_override') {
          body.related_record_fields.approved_principal = formData.approved_principal
        } else if (managerApprovalType?.code_key == 'mgr_approval_type_lead_term_override') {
          body.related_record_fields.apr = formData.apr
          body.related_record_fields.approved_principal = formData.approved_principal
        } else if (managerApprovalType?.code_key == 'mgr_approval_type_refinance') {
          body.related_record_fields.discount_principal = formData.discount_principal
          body.related_record_fields.discount_interest = formData.discount_interest ?? 0
          body.related_record_fields.additional_funds = formData.additional_funds ?? 0
          body.related_record_fields.apr = formData.apr
        }
      } else if (approvalType == "declined") {
        if (formData.description == "") {
          setError("description", { type: "manual", message: "Description is required when Declining" });
          setSaving(false)
          return;
        }
        request.setEndpoint(`${process.env.REACT_APP_API_DOMAIN}/manager_approval/${recordId}/decline`)
        body = {
          mgr_approval_status_dropdown_option_id: dropdownOptions.find((x) => x?.code_key === 'mgr_approval_status_declined')?.id,
          description: formData.description,
        }
      }
      const response = await request.modify(body)

      if (response.status === 200 || response.status === 201) {
        dispatch(triggerRerender({ subpanelId: 'manager_approval_list_view' }));
        setSaving(false)
        handleCancel()
        dispatch(setNotification({ type: 'success', body: response?.data?.message }))
      } else {
        setSaving(false)
        dispatch(setNotification({ type: 'error', body: `Error: ${response.status}` }))
      }
    } catch (error) {
      setSaving(false)
      const errorMessage = error?.response?.data?.message ?? error?.response?.data?.messageExtreme
      dispatch(setNotification({ type: 'error', body: errorMessage }))
    }
  }

  useEffect(() => {
    if (managerApprovalStatus?.code_key === 'mgr_approval_status_approved' || managerApprovalStatus?.code_key === 'mgr_approval_status_declined') {
      setActionDisabled(true)
    } else {
      setActionDisabled(false)
    }
  }, [managerApprovalStatus])

  return modal.is_active && modal.type === 'manager_approval_modal' ? (
    <form onSubmit={handleSubmit(onSubmit)} id="manager_approval_modal_form">
      <div className="modal is-active">
        <div className="modal-background"></div>
        <div className="modal-card column is-5">
          <header className="modal-card-head py-3">
            <p className="modal-card-title is-capitalized">
              {responseData && managerApprovalType?.name}
            </p>
            <button className="delete" aria-label="close" onClick={handleCancel}></button>
          </header>
          <section
            className="modal-card-body modal-card-body pt-1 pb-1"
            style={{ minHeight: "110px", overflow: 'hidden' }}
          >
            {!responseData && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  <span className="has-text-center"><Loading /></span>
                </div>
              </div>
            )}
            {responseData && (
              <div className="columns mb-0 is-flex-direction-column">
                <div className="column">
                  {actionDisabled && managerApprovalStatus?.code_key == "mgr_approval_status_approved" && (
                    <h3 className="has-text-success title is-5">{"Already Approved By " + responseData?.manager_user_label + "."}</h3>
                  )}
                  {actionDisabled && managerApprovalStatus?.code_key == "mgr_approval_status_declined" && (
                    <h3 className="has-text-danger title is-5">{"Already Declined By " + responseData?.manager_user_label + "."}</h3>
                  )}

                  {managerApprovalType?.code_key == 'mgr_approval_type_defer_count_override' && <DeferCountOverrideForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_credit' && <CreditForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_discount' && <DiscountForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_settlement_offer' && <SettlementOfferForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_lead_term_override' && <LeadTermOverrideForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_refinance' && <RefinanceForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}
                  {managerApprovalType?.code_key == 'mgr_approval_type_bank_account' && <BankAccountForm control={control} responseData={responseData} actionDisabled={actionDisabled} />}

                  <h3>Reason for approve or decline:</h3>
                  <div className="control">
                    <Controller
                      name="description"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <textarea
                          {...field}
                          maxLength={255}
                          className="textarea"
                          disabled={actionDisabled}
                          rows={1}
                        />
                      )}
                    />
                  </div>
                  <p className="has-text-danger is-size-7 lox-capitalize-first-letter">
                    {errors.description && <span>{errors.description.message ? errors.description.message : "Description is required"}</span>}
                  </p>
                </div>
              </div>
            )}
          </section>
          <footer className="modal-card-foot is-justify-content-end py-1">
            <div className="lox-form__buttons-wrapper has-text-right">
              <button type="button" className="button is-small has-background-grey has-text-white" onClick={handleCancel}>Cancel</button>

              {!actionDisabled && isApprovalDataLoaded && (
                <>
                  <button type="submit" className="button is-danger is-small" disabled={saving && approvalType == 'declined' && 'disabled'} onClick={() => setApprovalType('declined')}>{(saving && approvalType == 'declined') ? 'Declining' : 'Decline'}</button>

                  <button type="submit" className="button is-success is-small" disabled={saving && approvalType == 'approved' && 'disabled'} onClick={() => setApprovalType('approved')}>{(saving && approvalType == 'approved') ? 'Approving' : 'Approve'}</button>
                </>
              )}
            </div>
          </footer>
        </div>
      </div>
    </form>
  ) : null;
}
